import getCookie from '@Utility/getCookie';
import AnalyticsManager from './AnalyticsManager';
import GoogleAnalyticsProvider from './providers/GoogleAnalyticsProvider';
import { EVENT_TYPES, EVENT_FIELDS } from './utils/events';

/**
 * Promotion Analytics utility for tracking promotion interactions
 * Handles cookie management for last promotion visited
 */
export default class PromotionAnalytics {
  constructor() {
    this.analytics = new AnalyticsManager();
    this.cookieName = 'tw_last_promotion';
    this.cookieMaxAge = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
    this.isInitialized = false;

    // Add Google Analytics provider
    const googleProvider = new GoogleAnalyticsProvider();
    this.analytics.addProvider(googleProvider);
  }

  /**
   * Initialize the promotion analytics system
   * @returns {Promise<boolean>}
   */
  async initialize() {
    const results = await this.analytics.initialize();
    this.isInitialized = results.success.length > 0;

    console.debug('Promotion Analytics initialized:', {
      successfulProviders: results.success,
      failedProviders: results.failed,
    });

    return this.isInitialized;
  }

  /**
   * Set a cookie with promotion data
   * @param {Object} promotionData - Promotion information
   * @private
   */
  setPromotionCookie(promotionData) {
    const cookieValue = JSON.stringify({
      promotion_id: promotionData.promotion_id,
      promotion_name: promotionData.promotion_name,
      ad_size: promotionData.ad_size,
      button_title: promotionData.button_title || null,
      timestamp: Date.now(),
    });

    const encoded = encodeURIComponent(cookieValue);
    const maxAge = this.cookieMaxAge / 1000;
    document.cookie = `${this.cookieName}=${encoded}; max-age=${maxAge}; path=/; SameSite=Lax; Secure`;
  }

  /**
   * Get the last promotion data from cookie
   * @returns {Object|null}
   */
  getLastPromotionData() {
    const cookieValue = getCookie(this.cookieName);

    if (!cookieValue) {
      return null;
    }

    try {
      const data = JSON.parse(decodeURIComponent(cookieValue));

      if (Date.now() - data.timestamp > this.cookieMaxAge) {
        return null;
      }

      return data;
    } catch (error) {
      console.warn('Failed to parse promotion cookie:', error);
      return null;
    }
  }

  /**
   * Clear the promotion cookie
   */
  clearPromotionCookie() {
    document.cookie = `${this.cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }

  /**
   * Track promotion impression (when user sees promotion card)
   * @param {Object} promotionData - Promotion information
   * @param {string} promotionData.promotion_id - Unique promotion identifier
   * @param {string} promotionData.promotion_name - Human readable promotion name
   * @param {string} promotionData.ad_size - Card variant (e.g., 'default', 'large', 'small')
   */
  trackPromotionImpression(promotionData) {
    if (!this.isInitialized) {
      console.warn('PromotionAnalytics not initialized');
      return;
    }

    const eventData = {
      [EVENT_FIELDS.PROMOTION_ID]: promotionData.promotion_id,
      [EVENT_FIELDS.PROMOTION_NAME]: promotionData.promotion_name,
      [EVENT_FIELDS.AD_SIZE]: promotionData.ad_size,
      [EVENT_FIELDS.PAGE_URI]: window.location.pathname,
      [EVENT_FIELDS.PAGE_TYPE]: 'promotion_impression',
    };

    this.analytics.trackEvent(EVENT_TYPES.PROMO_IMPRESSION, eventData);

    this.setPromotionCookie(promotionData);
  }

  /**
   * Track promotion click (when user clicks any button in promotion card)
   * @param {Object} promotionData - Promotion information
   * @param {string} promotionData.promotion_id - Unique promotion identifier
   * @param {string} promotionData.promotion_name - Human readable promotion name
   * @param {string} promotionData.ad_size - Card variant
   * @param {string} promotionData.button_title - Text/label of the clicked button
   * @param {string} promotionData.click_category - Type of click (e.g., 'primary_cta', 'secondary_cta', 'modal_trigger')
   */
  trackPromotionClick(promotionData) {
    if (!this.isInitialized) {
      console.warn('PromotionAnalytics not initialized');
      return;
    }

    const eventData = {
      [EVENT_FIELDS.PROMOTION_ID]: promotionData.promotion_id,
      [EVENT_FIELDS.PROMOTION_NAME]: promotionData.promotion_name,
      [EVENT_FIELDS.AD_SIZE]: promotionData.ad_size,
      [EVENT_FIELDS.CLICK_CATEGORY]: promotionData.click_category,
      [EVENT_FIELDS.BUTTON_TITLE]: promotionData.button_title,
      [EVENT_FIELDS.PAGE_URI]: window.location.pathname,
      [EVENT_FIELDS.PAGE_TYPE]: 'promotion_click',
    };

    this.analytics.trackEvent(EVENT_TYPES.PROMO_CLICK, eventData);

    this.setPromotionCookie({
      ...promotionData,
      button_title: promotionData.button_title,
    });
  }

  /**
   * Track promotion VDP visit (when user visits vehicle detail page after promotion interaction)
   * @param {Object} vehicleData - Vehicle information from the VDP
   */
  trackPromotionVdpVisit(vehicleData = {}) {
    if (!this.isInitialized) {
      console.warn('PromotionAnalytics not initialized');
      return;
    }

    const lastPromotion = this.getLastPromotionData();

    if (!lastPromotion) {
      return;
    }

    const eventData = {
      [EVENT_FIELDS.PROMOTION_ID]: lastPromotion.promotion_id,
      [EVENT_FIELDS.PROMOTION_NAME]: lastPromotion.promotion_name,
      [EVENT_FIELDS.AD_SIZE]: lastPromotion.ad_size,
      [EVENT_FIELDS.BUTTON_TITLE]: lastPromotion.button_title,
      [EVENT_FIELDS.PAGE_URI]: window.location.pathname,
      [EVENT_FIELDS.PAGE_TYPE]: 'vdp',
      [EVENT_FIELDS.VEHICLE_STOCKID]: vehicleData.stock_id,
      [EVENT_FIELDS.VEHICLE_MAKE]: vehicleData.make,
      [EVENT_FIELDS.VEHICLE_MODEL]: vehicleData.model,
      [EVENT_FIELDS.VEHICLE_YEAR]: vehicleData.year,
    };

    this.analytics.trackEvent(EVENT_TYPES.PROMO_VDP_VISIT, eventData);
  }

  /**
   * Track promotion conversion (when user submits contact form after promotion interaction)
   * @param {FormData|Object} formData - Form data from the conversion
   */
  trackPromotionConversion(formData) {
    if (!this.isInitialized) {
      console.warn('PromotionAnalytics not initialized');
      return;
    }

    const lastPromotion = this.getLastPromotionData();

    if (!lastPromotion) {
      return;
    }

    const eventData = {
      [EVENT_FIELDS.PROMOTION_ID]: lastPromotion.promotion_id,
      [EVENT_FIELDS.PROMOTION_NAME]: lastPromotion.promotion_name,
      [EVENT_FIELDS.AD_SIZE]: lastPromotion.ad_size,
      [EVENT_FIELDS.BUTTON_TITLE]: lastPromotion.button_title,
      [EVENT_FIELDS.PAGE_URI]: window.location.pathname,
      [EVENT_FIELDS.PAGE_TYPE]: 'promotion_conversion',
    };

    // Add form data if available
    if (formData instanceof FormData) {
      eventData[EVENT_FIELDS.LEAD_ID] = formData.get('lead_id');
      eventData[EVENT_FIELDS.DEPARTMENT] = formData.get('department');
      eventData[EVENT_FIELDS.VEHICLE_STOCKID] = formData.get('stock_id');
      eventData[EVENT_FIELDS.VEHICLE_MAKE] = formData.get('make');
      eventData[EVENT_FIELDS.VEHICLE_MODEL] = formData.get('model');
      eventData[EVENT_FIELDS.VEHICLE_YEAR] = formData.get('year');
    } else if (formData && typeof formData === 'object') {
      eventData[EVENT_FIELDS.LEAD_ID] = formData.lead_id;
      eventData[EVENT_FIELDS.DEPARTMENT] = formData.department;
      eventData[EVENT_FIELDS.VEHICLE_STOCKID] = formData.stock_id;
      eventData[EVENT_FIELDS.VEHICLE_MAKE] = formData.make;
      eventData[EVENT_FIELDS.VEHICLE_MODEL] = formData.model;
      eventData[EVENT_FIELDS.VEHICLE_YEAR] = formData.year;
    }

    this.analytics.trackEvent(EVENT_TYPES.PROMO_CONVERSION, eventData);

    // Clear the promotion cookie after conversion to avoid double-counting
    this.clearPromotionCookie();
  }

  /**
   * Track promotion landing page visit
   * @param {Object} promotionData - Promotion information
   */
  trackPromotionLandingPageVisit(promotionData) {
    if (!this.isInitialized) {
      console.warn('PromotionAnalytics not initialized');
      return;
    }

    const eventData = {
      [EVENT_FIELDS.PROMOTION_ID]: promotionData.promotion_id,
      [EVENT_FIELDS.PROMOTION_NAME]: promotionData.promotion_name,
      [EVENT_FIELDS.PAGE_URI]: window.location.pathname,
      [EVENT_FIELDS.PAGE_TYPE]: 'promotion_landing_page',
    };

    this.analytics.trackEvent(EVENT_TYPES.PROMO_LP_VISIT, eventData);

    this.setPromotionCookie(promotionData);
  }

  /**
   * Get the underlying analytics manager for advanced usage
   * @returns {AnalyticsManager}
   */
  getAnalyticsManager() {
    return this.analytics;
  }

  /**
   * Check if promotion analytics is initialized
   * @returns {boolean}
   */
  isReady() {
    return this.isInitialized;
  }
}
